import {
  Component,
  Mixins,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { IProduct, IProductCategory, IProductPrice } from "@/models/products";
import { DataModules } from "@/store/modules/data";
import { IAccount } from "@/models/accounts";
import { ICurrency } from "@/models/constants";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { PriceDisplayTypes } from "@/data/enums/prices";
import { IBasket } from "@/models/baskets";
import {
  IAvailableAttributeCategory,
  IAvailableOptionCategory,
  IAvailableTerm,
  IProductConfig,
  IProductConfigProviderData,
  IProductConfigProviderMethods
} from "@/models/providers/products";
import { IImage } from "@/store/types";
import { BrandTaxType, ProductOrderTypes } from "@/data/enums";
import { DefaultPaymentPeriod } from "@/data/enums/products";
import { RegexMatch } from "@/data/enums/router";
import _ from "@/boot/lodash";

@Component({
  computed: {
    ...mapState({
      product(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },
      productScope(state, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      }
    })
  }
})
export default class ProductConfigProvider extends Mixins(
  RequestPromises,
  Context
) {
  // Props

  @Prop({ default: "" })
  public accountId!: IAccount["id"];

  @Prop({ default: "" })
  public basketId!: IBasket["id"];

  @Prop({ default: null })
  public basketProduct!: any;

  @Prop({ required: true })
  public currencyId!: ICurrency["id"];

  @Prop({ default: 0 })
  public billingTerm!: number;

  @Prop({ default: 1 })
  public quantity!: number;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ default: () => [] })
  public coupons!: string[];

  @Prop({ required: true })
  public taxType!: BrandTaxType;

  @Prop({ required: false })
  public omitPromotions!: boolean;

  @Prop({ default: true })
  public formatConfigTotal!: boolean;

  @Provide()
  public productConfigProviderData(): IProductConfigProviderData {
    return {
      availableAttributes: this.availableAttributes,
      availableOptions: this.availableOptions,
      availableTerms: this.availableTerms,
      categories: this.categories,
      config: this.config,
      configTotalFormatted: this.configTotalFormatted,
      currencyId: this.currencyId,
      description: this.description,
      from: this.from,
      images: this.images,
      isProcessing: this.isProcessing,
      hasPriceOverrideOption: this.hasPriceOverrideOption,
      maxQuantity: this.product.max_order_quantity,
      name: this.name,
      orderType: this.product.order_type,
      prices: this.prices,
      pricesFormatted: this.pricesFormatted,
      product: this.product,
      productId: this.productId,
      quantities: this.quantities,
      quantityStep: this.product.unit_quantity,
      selectedAttributes: this.selectedAttributes,
      selectedBillingTerm: this.selectedBillingTerm,
      selectedOptions: this.selectedOptions,
      taxType: this.taxType
    };
  }

  @Provide()
  public productConfigProviderMethods: IProductConfigProviderMethods = {
    selectBillingTerm: this.selectBillingTerm
  };

  // Data

  public configTotalFormatted = "";
  public isLoading = true;
  public isProcessing = false;
  public prices = {};
  public pricesFormatted = {};
  public product!: IProduct;
  public productScope!: string;
  public quantities = {};
  public selectedAttributes = {};
  public selectedBillingTerm: number | null = null;
  public selectedOptions = {};
  public brandConfig = {};
  // Computed

  public get categories() {
    const category = _.get(this.product, "category");
    if (!category) return [];
    return (function getParent(category: IProductCategory) {
      let categories = [
        {
          ..._.pick(category, ["id", "level"]),
          name: category.name_translated || category.name
        }
      ];
      const parent = _.get(category, "top_category");
      if (parent) categories = categories.concat(getParent(parent));
      return categories;
    })(category).reverse() as IProductCategory[];
  }

  public get description() {
    return (
      _.get(this.product, "description_translated") ||
      _.get(this.product, "description") ||
      _.get(this.product, "short_description_ranslated") ||
      _.get(this.product, "short_description")
    );
  }

  public get images() {
    return _.get(this.product, "images", []) as IImage[];
  }

  public get name() {
    return (
      _.get(this.product, "name_translated") || _.get(this.product, "name")
    );
  }

  public get from() {
    const asLMFP = // asLMFP = as Lowest Monthly From Price
      this.$store.getters["brand/config"][
        BrandConfigKeys.PRICE_DISPLAY_TYPE
      ] === PriceDisplayTypes.MONTHLY_FROM;

    const term = _.first(
      _.orderBy(
        this.availableTerms,
        [
          term => {
            return asLMFP ? term.monthly_price_from : term.billing_cycle_months;
          }
        ],
        ["asc"]
      )
    ) as any;

    return this.$store.getters["data/catalogue/productTermFromPrice"](
      term,
      asLMFP
    );
  }

  public get availableTerms(): IAvailableTerm[] {
    const terms = _.get(this.product, "prices") || [];
    return _.map(_.orderBy(terms, "billing_cycle_months"), term => {
      return {
        ..._.pick(term, [
          "billing_cycle_months",
          "mixed_promotions",
          "monthly_price_from_discounted_formatted",
          "monthly_price_from",
          "monthly_price_from_formatted",
          "price",
          "price_discounted",
          "price_discounted_formatted",
          "price_formatted"
        ]),
        custom_price: _.get(
          this.prices,
          `${this.productId}_${term.billing_cycle_months}`,
          null
        ),
        custom_price_formatted: _.get(
          this.pricesFormatted,
          `${this.productId}_${term.billing_cycle_months}.formatted`,
          null
        ),
        ...this.$store.getters["data/catalogue/productTermSavings"](term),
        hasMixedPromotions: _.some(_.get(this.product, "prices", []), [
          "mixed_promotions",
          true
        ]),
        promotionsByName: _.map(
          term.promotions || [],
          promo => promo.name_translated || promo.name
        )
      };
    });
  }

  public get availableOptions(): IAvailableOptionCategory[] {
    if (_.isEmpty(this.product)) return [];
    return _.filter(
      _.map(
        _.orderBy(_.uniqBy(this.product.products_options, "category.id"), [
          "attached_order"
        ]),
        ({ category }: { category: IProductCategory }) => {
          return {
            id: category.id,
            name: category.name_translated || category.name,
            multiple: category.multiple,
            required: category.required,
            price_override: category.price_override,
            options: _.map(
              _.orderBy(
                _.filter(this.product.products_options, option => {
                  return (
                    option.category_id === category.id &&
                    !!_.find(option.prices, price =>
                      [0, this.selectedBillingTerm].includes(
                        price.billing_cycle_months
                      )
                    )
                  );
                }),
                ["attached_order"]
              ),
              option => {
                this.$set(
                  this.quantities,
                  option.id,
                  this.quantities[option.id] || option.unit_quantity || 1
                );
                const price = _.find(option.prices, price =>
                  [0, this.selectedBillingTerm].includes(
                    price.billing_cycle_months
                  )
                );
                return {
                  name: option.name_translated || option.name,
                  ..._.pick(option, [
                    "id",
                    "order_type",
                    "unit_quantity",
                    "max_order_quantity"
                  ]),
                  ..._.pick(price as IProductPrice, [
                    "mixed_promotions",
                    "billing_cycle_months",
                    "price",
                    "price_discounted",
                    "price_formatted",
                    "price_discounted_formatted",
                    "promotions"
                  ]),
                  custom_price: _.get(this.prices, option.id, null) as number,
                  custom_price_formatted: _.get(
                    this.pricesFormatted,
                    `${option.id}.formatted`,
                    null
                  ) as string,
                  ...this.$store.getters["data/catalogue/productTermSavings"](
                    price
                  ),
                  hasMixedPromotions: _.some(option.prices, [
                    "mixed_promotions",
                    true
                  ]),
                  promotionsByName: _.uniq(
                    _.flatten(
                      _.map(option.prices, price => {
                        return _.map(
                          price.promotions || [],
                          promo => promo.name_translated || promo.name
                        );
                      })
                    )
                  )
                };
              }
            )
          };
        }
      ),
      category => !!category.options.length
    );
  }

  public get availableAttributes(): IAvailableAttributeCategory[] {
    if (_.isEmpty(this.product)) return [];
    return _.map(
      _.orderBy(_.uniqBy(this.product.products_attributes, "category.id"), [
        "attached_order"
      ]),
      ({ category }: { category: IProductCategory }) => {
        return {
          id: category.id,
          name: category.name_translated || category.name,
          multiple: category.multiple,
          required: category.required,
          attributes: _.map(
            _.orderBy(
              _.filter(this.product.products_attributes, [
                "category_id",
                category.id
              ]),
              ["attached_order"]
            ),
            attribute => {
              return {
                id: attribute.id,
                name: attribute.name_translated || attribute.name
              };
            }
          )
        };
      }
    );
  }

  public get priceOverrideOptionCategory() {
    return this.availableOptions.find(
      i =>
        _.has(this.selectedOptions, i.id) &&
        !_.isEmpty(this.selectedOptions[i.id]) &&
        i.price_override
    );
  }

  public get hasPriceOverrideOption() {
    return !!this.priceOverrideOptionCategory;
  }

  public get config(): IProductConfig | null {
    const term = _.find(this.availableTerms, [
      "billing_cycle_months",
      this.selectedBillingTerm
    ]);
    if (_.isEmpty(this.product) || !term) return null;
    return {
      product_id: this.product.id,
      quantity:
        _.get(this.quantities, this.product.id, this.product.unit_quantity) ||
        1,
      billing_cycle_months: this.selectedBillingTerm as number,
      ...(!_.isNil(term.custom_price)
        ? {
            selling_price: term.custom_price
          }
        : null),
      total: _.first(
        _.without([term.custom_price, term.price_discounted, term.price], null)
      ) as number,
      options: _.map(
        _.compact(
          _.flatten(
            _.map(this.availableOptions, category => {
              const selected = this.selectedOptions[category.id];
              return _.intersectionWith(
                category.options,
                _.isArray(selected) ? selected : [selected],
                (option, optionId) => option.id === optionId
              );
            })
          )
        ),
        opt => {
          // Option price hierarchy (use custom first, then discounted etc)
          const price = [opt.custom_price, opt.price_discounted, opt.price];
          const unitTotal = _.first(_.without(price, null)) as number;
          return {
            billing_cycle_months: opt.billing_cycle_months,
            order_type: opt.order_type,
            product_id: opt.id,
            ...(_.isNumber(opt.custom_price)
              ? { selling_price: opt.custom_price }
              : null),
            total: unitTotal * this.quantities[opt.id],
            unit_quantity: this.quantities[opt.id],
            unit_total: unitTotal
          };
        }
      ),
      attributes: _.map(
        _.compact(
          _.flatten(
            _.map(this.availableAttributes, category => {
              const selected = this.selectedAttributes[category.id];
              return _.intersectionWith(
                category.attributes,
                _.isArray(selected) ? selected : [selected],
                (attribute, attributeId) => attribute.id === attributeId
              );
            })
          )
        ),
        attribute => ({
          product_id: attribute.id
        })
      ),
      promotions: _.map(_.compact(this.coupons), promocode => ({
        promocode
      }))
    };
  }

  public get configTotals(): number[] {
    if (!this.config) return [];
    return [
      // Quantifiable
      ..._.flatten(
        Array(this.config.quantity).fill([
          this.hasPriceOverrideOption ? 0 : this.config.total,
          ..._.map(
            _.filter(
              this.config.options,
              o => o.order_type !== ProductOrderTypes.SINGLE_OPTION
            ),
            "total"
          )
        ])
      ),
      // Unquantifiable
      ..._.map(
        _.filter(
          this.config.options,
          o => o.order_type === ProductOrderTypes.SINGLE_OPTION
        ),
        "total"
      )
    ];
  }

  public get productBasePrice() {
    return this.availableTerms.find(
      term => term.billing_cycle_months === this.selectedBillingTerm
    )?.price;
  }

  public get pathSubproductIds() {
    let pathSubproductIds = [];

    _.forEach(
      _.get(this.$route, "query", {}),
      (queryValue: string, queryKey: string) => {
        if (queryKey === QUERY_PARAMS.SUBPRODUCT_IDS) {
          pathSubproductIds = queryValue.split(",") as [];
        }
      }
    );

    return pathSubproductIds;
  }

  // Lifecycle methods

  public render(createElement: CreateElement) {
    if (this.isLoading)
      return createElement("is-loading", { props: { if: true } });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $productConfigData: this.productConfigProviderData(),
        $productConfigMethods: this.productConfigProviderMethods
      })
    );
  }

  public async created() {
    try {
      await Promise.all([
        this.getProduct(),
        this.$store.dispatch("constants/getCurrencies"),
        this.$store.dispatch("constants/getBillingCycles")
      ]);
      this.brandConfig = await this.getBrandConfig();
      this.setDefaultConfig();
    } catch (error) {
      const errorCode = _.get(error, "response.status");
      if ([403, 404, 409].includes(errorCode)) this.$emit("abort", error);
    } finally {
      this.$emit("loaded");
      this.isLoading = false;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  }

  // Methods

  public getBrandConfig() {
    return this.$store.dispatch("brand/getConfig", {
      keys: [BrandConfigKeys.PRICE_TAX_PRICE_DEFAULT_PAYMENT_PERIOD],
      brandId: this.$store.state.auth.admin.brandId
    });
  }

  public getProduct(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      isBasketContext: true,
      clientApi: true,
      params: _.merge(
        {},
        { currency_id: this.currencyId },
        this.omitPromotions ? { omit_promotions: 1 } : {},
        this.coupons ? { promotions: this.coupons.join() } : {},
        this.basketId && this.basketId !== RegexMatch.NEW
          ? { basket_id: this.basketId }
          : {},
        this.basketProduct ? { basket_product_id: this.basketProduct.id } : {},
        this.isAdmin && this.accountId ? { account_id: this.accountId } : {}
      ),
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public setDefaultConfig() {
    const product = this.basketProduct;

    if (!product) {
      this.selectBillingTerm(
        this.calculateBillingTerm(this.product.default_payment_period)
      );
      this.setProductQuantity(this.product);
      this.preselectOptionsAndAttributes();
      this.preselectSingleMandatoryOptions();
      return;
    }

    // Set selected billing term
    this.selectBillingTerm(product.billing_cycle_months);
    // Set quantity
    this.$set(this.quantities, product.product_id, product.quantity);
    // Set custom selling price
    if (product.selling_price_converted !== this.productBasePrice) {
      const priceKey = `${product.product_id}_${product.billing_cycle_months}`;
      this.$set(this.prices, priceKey, product.selling_price_converted);
      // Set formatted value upfront to avoid unnecessary calculate calls
      this.$set(this.pricesFormatted, priceKey, {
        price: product.selling_price_converted,
        formatted: product.selling_price_formatted
      });
    }

    // Set selected options
    _.each(product.options, option => {
      const category = option.product.category;

      const hasSingleOption =
        this.$_.get(
          this.$_.find(this.availableOptions, ["id", category.id]),
          "options",
          []
        ).length === 1;

      const result =
        category.multiple || hasSingleOption
          ? _.concat(this.$_.get(this.selectedOptions, `${category.id}`, []), [
              option.product_id
            ])
          : option.product_id;

      this.$set(this.selectedOptions, category.id, result);

      // Set custom prices
      const options = _.flatten(this.availableOptions.map(i => i.options));

      const optionBasePrice = options.find(
        i =>
          i.id === option.product_id &&
          [this.selectedBillingTerm, 0].includes(i.billing_cycle_months)
      )?.price;

      if (
        option.selling_price_converted !== optionBasePrice &&
        !_.isNull(optionBasePrice)
      ) {
        this.$set(
          this.prices,
          option.product_id,
          option.selling_price_converted
        );
        // Set formatted value upfront to avoid unnecessary calculate calls
        this.$set(this.pricesFormatted, option.product_id, {
          price: option.selling_price_converted,
          formatted: option.selling_price_formatted
        });
      }
      // Set quantity
      this.$set(this.quantities, option.product_id, option.unit_quantity);
    });

    // Set selected attributes
    _.each(product.attributes, attribute => {
      const category = attribute.product.category;

      const hasSingleAttribute =
        this.$_.get(
          this.$_.find(this.availableAttributes, ["id", category.id]),
          "attributes",
          []
        ).length === 1;

      this.$set(
        this.selectedAttributes,
        category.id,
        category.multiple || hasSingleAttribute
          ? _.union(this.selectedAttributes[category.id], [
              attribute.product_id
            ])
          : attribute.product_id
      );
    });
  }

  public preselectSingleMandatoryOptions() {
    for (const category of _.filter(this.availableOptions, category => {
      // Return mandatory categories with only one available option
      return category.required && category.options.length === 1;
    })) {
      const optionId = category.options[0].id;
      this.$set(this.selectedOptions, category.id, [optionId]);
    }
  }

  public preselectOptionsAndAttributes() {
    _.forEach(this.pathSubproductIds, subproductId => {
      const optionCategory = _.find(this.availableOptions, availableOption => {
        return _.some(availableOption.options, ["id", subproductId]);
      });

      if (optionCategory) {
        if (optionCategory.multiple || optionCategory.options.length === 1) {
          const selectedOptions = this.selectedOptions[optionCategory.id] || [];
          selectedOptions.push(subproductId);
          this.$set(this.selectedOptions, optionCategory.id, selectedOptions);
        } else {
          this.$set(this.selectedOptions, optionCategory.id, subproductId);
        }

        this.setProductQuantity(
          _.find(
            _.get(
              _.find(this.availableOptions, availableOption => {
                return _.some(availableOption.options, ["id", subproductId]);
              }),
              "options",
              []
            ),
            ["id", subproductId]
          ),
          false
        );
        return;
      }

      const attributeCategory = _.find(
        this.availableAttributes,
        availableAttribute => {
          return _.some(availableAttribute.attributes, ["id", subproductId]);
        }
      );

      if (attributeCategory) {
        if (
          attributeCategory.multiple ||
          attributeCategory.attributes.length === 1
        ) {
          const selectedAttributes =
            this.selectedAttributes[attributeCategory.id] || [];
          selectedAttributes.push(subproductId);
          this.$set(
            this.selectedAttributes,
            attributeCategory.id,
            selectedAttributes
          );
        } else {
          this.$set(
            this.selectedAttributes,
            attributeCategory.id,
            subproductId
          );
        }
      }
    });
  }

  public setProductQuantity(product, mainProduct = true) {
    _.forEach(
      _.get(this.$route, "query", {}),
      (queryValue, queryKey: string) => {
        mainProduct;

        if (
          (mainProduct && queryKey !== "qty") ||
          (!mainProduct &&
            (!queryKey.includes(QUERY_PARAMS.SUBPRODUCT_QUANTITY) ||
              !queryKey.includes(product.id)))
        )
          return;

        const productQty = _.toNumber(queryValue);

        if (
          product.order_type === ProductOrderTypes.QUANTITY_BASED &&
          product.unit_quantity !== 1 &&
          productQty % product.unit_quantity
        ) {
          this.$set(this.quantities, product.id, product.unit_quantity);
          return;
        }

        if (
          (product.order_type === ProductOrderTypes.SINGLE_OPTION ||
            product.order_type === ProductOrderTypes.INHERIT) &&
          productQty !== 1
        ) {
          this.$set(this.quantities, product.id, 1);
          return;
        }

        this.$set(this.quantities, product.id, productQty);
      }
    );
  }

  public calculateBillingTerm(period: DefaultPaymentPeriod) {
    const firstAvailableTerm = _.get(
      this.availableTerms,
      `0.billing_cycle_months`
    ) as number;

    let billingCycleMonths = firstAvailableTerm;

    let isBillingTermAvailable = _.some(this.availableTerms, [
      "billing_cycle_months",
      this.billingTerm || billingCycleMonths
    ]);

    if (this.billingTerm && isBillingTermAvailable)
      billingCycleMonths = this.billingTerm;
    else if (period === DefaultPaymentPeriod.HIGHEST_PRICE)
      billingCycleMonths = this.highestPriceTerm?.billing_cycle_months || 0;
    else if (period === DefaultPaymentPeriod.LOWEST_PRICE)
      billingCycleMonths = this.lowestPriceTerm?.billing_cycle_months || 0;
    else if (period === DefaultPaymentPeriod.LOWEST_MONTHLY_PRICE)
      billingCycleMonths =
        this.lowestMonthlyPriceTerm?.billing_cycle_months || 0;
    else if (period === DefaultPaymentPeriod.INHERIT_FROM_BRAND)
      billingCycleMonths = this.calculateBillingTerm(
        this.$store.getters["brand/config"][
          BrandConfigKeys.PRICE_TAX_PRICE_DEFAULT_PAYMENT_PERIOD
        ]
      );

    isBillingTermAvailable = _.some(this.availableTerms, [
      "billing_cycle_months",
      billingCycleMonths
    ]);

    return isBillingTermAvailable ? billingCycleMonths : firstAvailableTerm;
  }

  public selectBillingTerm(term: IProduct["billing_cycle_months"]) {
    this.selectedBillingTerm = term;
  }

  public get lowestPriceTerm() {
    return _.minBy(this.availableTerms, term => term.price || 0);
  }

  public get lowestMonthlyPriceTerm() {
    return _.minBy(this.availableTerms, term => term.monthly_price_from || 0);
  }

  public get highestPriceTerm() {
    return _.maxBy(this.availableTerms, term => term.price || 0);
  }

  // Watchers

  @Watch("prices", { deep: true })
  public async calculateFormattedValues(prices) {
    const formatted = {};
    for (const key in prices) {
      const price = prices[key];
      const previous = this.pricesFormatted[key] || {};
      formatted[key] = {
        price,
        formatted:
          price === previous.price
            ? previous.formatted
            : await this.$store.dispatch("api/calculate", {
                prices: [price],
                currency_id: this.currencyId,
                returnOnly: "total_formatted"
              })
      };
    }
    this.$set(this, "pricesFormatted", formatted);
  }

  @Watch("configTotals")
  public async calculateConfigTotal(prices, oldPrices) {
    if (!prices || !prices.length || !this.formatConfigTotal) return;
    if (_.sum(prices) !== _.sum(oldPrices) || !this.configTotalFormatted) {
      let formatted = this.configTotalFormatted;
      try {
        this.isProcessing = true;
        formatted = await this.$store.dispatch("api/calculate", {
          prices,
          currency_id: this.currencyId,
          returnOnly: "total_formatted"
        });
      } finally {
        this.isProcessing = false;
        this.$set(this, "configTotalFormatted", formatted);
      }
    }
  }

  @Watch("config", { deep: true })
  public emitNewConfig(config) {
    this.$emit("input", config);
  }
}
